import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/core';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { MY_REVIEWS_TABS, MyReviewsTab } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useUrlTab } from '@/hooks/use-linked-record';
import { SubmissionsList } from '@/modules/performance/hr-my-submissions';
import { PeerRequestsList } from '@/modules/performance/hr-peer-requests';
import { useMyReviewCounts } from '../../hooks/use-my-review-counts';

const { REQUESTS, SUBMISSIONS } = MY_REVIEWS_TABS;

/**
 * Performance › My Reviews (prototype `my-reviews`): one tab row, Requests (the peer lists I'm asked to
 * choose) and Submissions (the review forms assigned to me), each with its count of waiting items. Each tab
 * shows only with its own permission; the tab lives in the URL (`?tab=`).
 */
export const MyReviewsPage = () => {
  const { t } = useTranslation();
  const canRequests = useHasPermission(HRM_PERMISSIONS.PERFORMANCE.PEER_REQUESTS_VIEW);
  const canSubmissions = useHasPermission(HRM_PERMISSIONS.PERFORMANCE.SUBMISSIONS_VIEW_OWN);
  const tabs: MyReviewsTab[] = [
    ...(canRequests ? [REQUESTS] : []),
    ...(canSubmissions ? [SUBMISSIONS] : []),
  ];
  const [tab, setTab] = useUrlTab(tabs, tabs[0] ?? REQUESTS);
  const counts = useMyReviewCounts({ requests: canRequests, submissions: canSubmissions });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('MY_REVIEWS')} />

      {tabs.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('NO_REVIEWS_TO_SHOW')}</p>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          {/* Full width on a phone, each tab an equal share. */}
          <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
            {canRequests && (
              <TabsTrigger value={REQUESTS} className="max-sm:flex-1" count={counts.requests}>
                {t('REQUESTS')}
              </TabsTrigger>
            )}
            {canSubmissions && (
              <TabsTrigger value={SUBMISSIONS} className="max-sm:flex-1" count={counts.submissions}>
                {t('SUBMISSIONS')}
              </TabsTrigger>
            )}
          </TabsList>
          {canRequests && (
            <TabsContent value={REQUESTS}>
              <PeerRequestsList />
            </TabsContent>
          )}
          {canSubmissions && (
            <TabsContent value={SUBMISSIONS}>
              <SubmissionsList />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
};
